import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, types } from "../api/index.js";
import { shortCommit, commitUrl, containerfileUrl, externalLinkIcon, apiLink } from "../utils/format.js";
import { displayCanonicalWithTags } from "../utils/canonical.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";
import "../components/copy-button.js";

const VERIFY_CMD_TEMPLATE = import.meta.env.VITE_VERIFY_IMAGE_CMD || "";

@customElement("details-tab")
export class DetailsTab extends LitElement {
  @property() imageName = "";
  @property() canonical = "";
  @property({ type: Array }) canonicals: string[] = [];
  @property({ type: Array }) tags: types.Tag[] = [];
  @state() private _arch = "";

  createRenderRoot() {
    return this;
  }

  private _task = new Task(this, {
    args: () => [this.imageName, this.canonical] as const,
    task: async ([name, canonical]) => {
      if (!canonical) throw new Error("No canonical");
      const data = await fetchApi<types.DetailsResponse>(`/v1/images/${name}/details/${canonical}`);
      const arches = Object.keys(data);
      if (arches.length && !arches.includes(this._arch)) {
        this._arch = arches[0];
      }
      return data;
    },
  });

  private _onCanonicalChange(e: Event) {
    this.dispatchEvent(new CustomEvent("canonical-change", { detail: (e.target as HTMLSelectElement).value }));
  }

  private _onArchChange(e: Event) {
    this._arch = (e.target as HTMLSelectElement).value;
  }

  /** Build the verify command by substituting {image} with the pull URL for the current canonical. */
  private get _verifyCommand(): string {
    if (!VERIFY_CMD_TEMPLATE) return "";
    const tag = this.tags.find((t) => t.canonical === this.canonical);
    return tag ? VERIFY_CMD_TEMPLATE.replace("{image}", tag.pull_url) : "";
  }

  private _configRows(data: types.ArchDetails): [string, string][] {
    const rows: [string, string][] = [
      ["User", data.user],
      ["Working Dir", data.working_dir],
      ["Entrypoint", JSON.stringify(data.entrypoint)],
      ["CMD", JSON.stringify(data.cmd)],
      ["Stop Signal", data.stop_signal],
    ];
    if (data.exposed_ports.length) rows.push(["Exposed Ports", data.exposed_ports.join(", ")]);
    if (data.volumes.length) rows.push(["Volumes", data.volumes.join(", ")]);
    return rows;
  }

  private _provenanceRows(data: types.ArchDetails): [string, unknown][] {
    return [
      ["SLSA Level", data.slsa_level],
      ["Build System", data.build_system],
      [
        "Source Repo",
        data.source_repo
          ? html`<a href=${data.source_repo} target="_blank"
              class="hover:text-gray-100 transition-colors"
              >${data.source_repo}${externalLinkIcon}</a>`
          : "—",
      ],
      [
        "Source Commit",
        data.source_commit && data.source_repo
          ? html`<a href=${commitUrl(data.source_repo, data.source_commit)} target="_blank" title=${data.source_commit}
              class="hover:text-gray-100 transition-colors"
              >${shortCommit(data.source_commit)}${externalLinkIcon}</a>`
          : data.source_commit
            ? shortCommit(data.source_commit)
            : "—",
      ],
      [
        "Containerfile",
        data.containerfile && data.source_repo && data.source_commit
          ? html`<a href=${containerfileUrl(data.source_repo, data.source_commit, data.containerfile)} target="_blank"
              class="hover:text-gray-100 transition-colors"
              >${data.containerfile}${externalLinkIcon}</a>`
          : data.containerfile || "—",
      ],
    ];
  }

  private _renderSection(title: string, rows: [string, unknown][], count?: number) {
    if (!rows.length) return "";
    return html`
      <tbody>
        <tr class="bg-hb-surface">
          <td colspan="2" class="py-2 px-3 text-gray-400 uppercase tracking-wide">
            ${title}${count !== undefined ? ` (${count})` : ""}
          </td>
        </tr>
        ${rows.map(
          ([k, v]) => html`
            <tr class="border-b border-hb-border/50">
              <td class="py-1.5 px-3 text-gray-400 font-mono break-all">${k}</td>
              <td class="py-1.5 px-3 text-gray-200 font-mono break-all">${v || "—"}</td>
            </tr>
          `,
        )}
      </tbody>
    `;
  }

  render() {
    return html`
      <div class="mb-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div>
            <label class="text-gray-500 text-sm mr-1">Tag:</label>
            <select
              .value=${this.canonical}
              @change=${this._onCanonicalChange}
              class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
            >
              ${this.canonicals.map((c) => html`<option value=${c} ?selected=${c === this.canonical}>${displayCanonicalWithTags(c, this.tags)}</option>`)}
            </select>
          </div>
          ${this._task.render({
            complete: (data) => {
              const arches = Object.keys(data);
              return arches.length > 1
                ? html`
                    <div>
                      <label class="text-gray-500 text-sm mr-1">Arch:</label>
                      <select
                        .value=${this._arch}
                        @change=${this._onArchChange}
                        class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
                      >
                        ${arches.map((a) => html`<option value=${a} ?selected=${a === this._arch}>${a}</option>`)}
                      </select>
                    </div>
                  `
                : "";
            },
          })}
        </div>
        ${this.canonical ? apiLink(`/v1/images/${this.imageName}/details/${this.canonical}`) : ""}
      </div>

      ${this._task.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) => html`<error-message .message=${String(e)}></error-message>`,
        complete: (data) => {
          const archData = data[this._arch];
          if (!archData) return html`<p class="text-gray-500 text-sm">No details available.</p>`;
          const envEntries = Object.entries(archData.env).sort(([a], [b]) => a.localeCompare(b));
          const labelEntries = Object.entries(archData.labels).sort(([a], [b]) => a.localeCompare(b));
          return html`
            <div class="space-y-6">
              <div class="border border-hb-border rounded-lg overflow-x-auto">
                <table class="w-full text-left text-xs">
                  ${archData.slsa_level ? this._renderSection("Provenance", this._provenanceRows(archData)) : ""}
                  ${this._renderSection("Container Config", this._configRows(archData))}
                  ${envEntries.length ? this._renderSection("Environment Variables", envEntries, envEntries.length) : ""}
                  ${this._renderSection("Labels", labelEntries, labelEntries.length)}
                </table>
              </div>

              ${archData.slsa_level && this._verifyCommand
                ? html`
                    <div>
                      <h3 class="text-gray-400 text-xs uppercase tracking-wide mb-2">Verify Image Signature</h3>
                      <div class="relative">
                        <pre
                          class="bg-hb-bg border border-hb-border rounded-lg p-3 pr-10 text-xs text-gray-300 overflow-x-auto"
                        >${this._verifyCommand}</pre>
                        <div class="absolute top-2 right-2">
                          <copy-button .value=${this._verifyCommand} label="verify command"></copy-button>
                        </div>
                      </div>
                    </div>
                  `
                : ""}
            </div>
          `;
        },
      })}
    `;
  }
}
